import {
  BaseActionObject,
  DoneInvokeEvent,
  StateNodeConfig,
  assign
} from "xstate4"
import { Context, StudyPlanTargets, Transitions } from "../types"
import { getServiceErrorMessage } from "../helpers"
import { ExistingPlanOptions } from "../../../types"
import { setSPV2RedirectUrl } from "../studyPlanV2/actions"
import {
  BASE_SP_V2_PATH,
  BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH
} from "../studyPlanV2/constants"

const NEED_OVERWRITE_MODE = "NEED_OVERWRITE_MODE"
const ZERO_INSTRUCTIONAL_TIME =
  "You cannot save a study plan with 0 hours of instructional time."

export const saveStudyPlanTransition: StateNodeConfig<
  Context,
  {},
  Transitions,
  BaseActionObject
> = {
  invoke: {
    id: "activatePlan",
    src: "activatePlan",
    onDone: [
      {
        target: `${BASE_SP_V2_PATH}`,
        actions: assign<Context>((ctx, evt) => ({
          studyPlan: null,
          studyPlansListBySubjects: null, // reset this to loading all plans including the newly saved plan
          seconds: null,
          existingPlanOption: ExistingPlanOptions.NOTSET,
          snackbar: {
            message: "Study Plan saved.",
            withUndo: false,
            variant: "success"
          },
          ...setSPV2RedirectUrl(
            ctx,
            evt,
            null,
            StudyPlanTargets.ManageStudyPlans
          )
        })),
        cond: (ctx, _evt) =>
          (ctx.selfRefScope.scope === "DISTRICT" ||
            ctx.selfRefScope.scope === "SCHOOL") &&
          ctx.isStudyPlanV2Enabled
      },
      {
        // maybe make it get_study_plans_transition then have that transition to see if previousParent === "save" and if so go to study_plans_overview
        target: "#StudyPlan.study_plans_overview",
        actions: assign<Context>({
          studyPlan: null,
          studyPlansListBySubjects: null, // reset this to loading all plans including the newly saved plan
          seconds: null,
          existingPlanOption: ExistingPlanOptions.NOTSET,
          snackbar: (_ctx, _evt) => ({
            message: "Study Plan saved.",
            withUndo: false,
            variant: "success"
          })
        }),
        cond: (ctx, _evt) =>
          ctx.selfRefScope.scope === "DISTRICT" ||
          ctx.selfRefScope.scope === "SCHOOL"
      },
      {
        target: "#StudyPlan.teacher_view",
        actions: assign<Context>({
          studyPlan: null,
          studyPlansListBySubjects: null, // reset this to loading all plans including the newly saved plan
          seconds: null,
          existingPlanOption: ExistingPlanOptions.NOTSET,
          snackbar: (_ctx, _evt) => ({
            message: "Study Plan saved.",
            withUndo: false,
            variant: "success"
          })
        }),
        cond: (ctx, _evt) => ctx.selfRefScope.scope === "TEACHER"
      }
    ],
    onError: [
      {
        // Stay on edit_plan page so user can modify hours.
        target: "#StudyPlan.edit_plan.idle",
        cond: (_ctx, evt) => evt.data?.message === ZERO_INSTRUCTIONAL_TIME,
        actions: assign<Context, DoneInvokeEvent<any>>({
          snackbar: (_ctx, evt) => ({
            message: evt.data,
            withUndo: false,
            variant: "warning"
          })
        })
      },
      {
        target: `${BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH}`,
        actions: assign<Context, DoneInvokeEvent<any>>((ctx, evt) => ({
          snackbar: {
            message: getServiceErrorMessage("activatePlan", {
              errorReason: evt.data.message
            }),
            withUndo: false,
            error: true
          },
          ...setSPV2RedirectUrl(
            ctx,
            evt,
            { action: { action: "replace" } },
            StudyPlanTargets.ManageStudyPlans
          )
        })),
        cond: (ctx, evt) =>
          evt.data.extension?.[0]?.code !== NEED_OVERWRITE_MODE &&
          ctx.isStudyPlanV2Enabled
      },
      {
        target: "#StudyPlan.study_plans_overview",
        cond: (_ctx, evt) =>
          evt.data.extension?.[0]?.code !== NEED_OVERWRITE_MODE,
        actions: assign<Context, DoneInvokeEvent<any>>({
          snackbar: (_ctx, evt) => ({
            message: getServiceErrorMessage("activatePlan", {
              errorReason: evt.data.message
            }), //
            withUndo: false,
            error: true
          })
        })
      },
      {
        target: "confirm_override",
        cond: (_ctx, evt) =>
          evt.data.extension?.[0]?.code === NEED_OVERWRITE_MODE,
        actions: assign<Context, DoneInvokeEvent<any>>((_ctx, evt) => {
          const { affectedPlansCounts } = evt.data.extension?.[0] || {}

          return {
            overrideSchoolsCount: affectedPlansCounts?.["SCHOOL"] ?? 0,
            overrideTeachersCount: affectedPlansCounts?.["TEACHER"] ?? 0,
            overrideClassesCount: affectedPlansCounts?.["CLASS"] ?? 0
          }
        })
      }
    ]
  }
}
